import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, Flag, CheckCircle2, Circle, CalendarPlus } from "lucide-react";

export default function Calendar() {
  const [events, setEvents] = useState(null);
  const [cursor, setCursor] = useState(new Date());

  useEffect(() => { api.get("/calendar").then(({ data }) => setEvents(data.events)); }, []);

  if (!events) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF7E67]" /></div>;

  const year = cursor.getFullYear(), month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const byDate = {};
  events.forEach((e) => { if (e.date) (byDate[e.date] = byDate[e.date] || []).push(e); });
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let dd = 1; dd <= days; dd++) cells.push(dd);
  const iso = (dd) => `${year}-${String(month + 1).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;

  return (
    <div className="space-y-8" data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-nunito font-extrabold tracking-tight text-4xl text-[#2D3748] sm:text-5xl">Calendar</h1>
        <button onClick={() => toast.info("Google Calendar sync needs OAuth credentials to activate — coming next.")}
          data-testid="connect-calendar-btn" className="flex items-center gap-2 rounded-full border border-[#FFE8E3] px-5 py-3 text-sm text-[#2D3748] hover:border-[#FF7E67]">
          <CalendarPlus className="h-4 w-4 text-[#FF7E67]" /> Connect Google Calendar
        </button>
      </div>

      <div className="rounded-3xl border border-[#FFE8E3] bg-white p-5 sm:p-7">
        <div className="mb-5 flex items-center justify-between">
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="rounded-full border border-[#FFE8E3] px-3 py-1 text-sm text-[#718096]">←</button>
          <p className="font-nunito text-lg font-bold text-[#2D3748]">{cursor.toLocaleString(undefined, { month: "long", year: "numeric" })}</p>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="rounded-full border border-[#FFE8E3] px-3 py-1 text-sm text-[#718096]">→</button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs text-[#718096]">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, idx) => <div key={idx} className="pb-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((dd, idx) => {
            const evs = dd ? (byDate[iso(dd)] || []) : [];
            return (
              <div key={idx} className={`min-h-[76px] rounded-lg border p-1.5 text-left ${dd ? "border-[#FFE8E3] bg-[#FFF9F5]" : "border-transparent"}`}>
                {dd && <p className="text-xs text-[#A0AEC0]">{dd}</p>}
                <div className="mt-1 space-y-1">
                  {evs.slice(0, 3).map((e, i) => (
                    <div key={i} className="flex items-center gap-1 truncate rounded bg-[#FFF4E6] px-1 py-0.5 text-[10px] text-[#4A5568]">
                      {e.type === "milestone" ? <Flag className="h-2.5 w-2.5 shrink-0 text-[#FF7E67]" /> : e.done ? <CheckCircle2 className="h-2.5 w-2.5 shrink-0 text-[#FF7E67]" /> : <Circle className="h-2.5 w-2.5 shrink-0 text-[#A0AEC0]" />}
                      <span className="truncate">{e.title}</span>
                    </div>
                  ))}
                  {evs.length > 3 && <p className="text-[10px] text-[#FF7E67]">+{evs.length - 3} more</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-xs text-[#718096]">Showing your tasks and goal milestones. External calendar sync activates once Google credentials are configured.</p>
    </div>
  );
}
